import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { Star, Minus, Plus, ShieldCheck, Truck, Leaf, ChevronRight, Award, Package, Clock } from "lucide-react";
import { toast } from "sonner";
import { ProductCard } from "./Home";
import WishlistHeart from "../../components/store/WishlistHeart";
import Reviews from "../../components/store/Reviews";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [variant, setVariant] = useState(null);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [related, setRelated] = useState([]);
  const { add } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then(r => {
      setP(r.data);
      setVariant(r.data.variants?.[0] || null);
      setImgIdx(0);
      return api.get(`/products?category=${r.data.category_slug}&limit=8`);
    }).then(r => setRelated(r.data.filter(x => x.slug !== slug).slice(0, 4))).catch(() => {});
  }, [slug]);

  if (!p) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-stone-500">Loading…</div>;

  const stock = variant?.stock ?? 0;
  const savings = variant && variant.cost_price ? Math.round(((variant.price - variant.cost_price)/variant.price)*100) : 0;

  return (
    <div className="relative">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: 80, right: -150 }} />
      <div className="blob blob-terra" style={{ width: 400, height: 400, bottom: 200, left: -150 }} />

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 relative z-10">
        <nav className="flex items-center gap-2 text-xs text-stone-500 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-[#3B2F2F]">Home</Link>
          <ChevronRight className="w-3 h-3"/>
          <Link to="/shop" className="hover:text-[#3B2F2F]">Shop</Link>
          <ChevronRight className="w-3 h-3"/>
          <Link to={`/shop/${p.category_slug}`} className="hover:text-[#3B2F2F] capitalize">{p.category_slug?.replace(/-/g," ")}</Link>
          <ChevronRight className="w-3 h-3"/>
          <span className="text-stone-800 truncate">{p.name}</span>
        </nav>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="aspect-square bg-gradient-to-br from-[#F5F0E6] via-[#FEF3E2] to-[#F5DEC7] rounded-3xl overflow-hidden relative shadow-xl shadow-[#3B2F2F]/10">
              <img src={p.images?.[imgIdx]} alt={p.name} className="w-full h-full object-cover" />
              {p.is_bestseller && (
                <div className="absolute top-4 left-4 bg-[#D4AF37] text-[#3B2F2F] text-[10px] font-outfit font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg">Best Seller</div>
              )}
              {p.is_new && (
                <div className="absolute top-4 right-4 bg-[#2E4A35] text-white text-[10px] font-outfit font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg">Just In</div>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              {p.images?.map((im, i) => (
                <button key={i} onClick={()=>setImgIdx(i)} data-testid={`thumb-${i}`}
                        className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${imgIdx===i?"border-[#D4AF37] scale-105":"border-transparent opacity-70 hover:opacity-100"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#D4AF37]/40 rounded-full px-3 py-1 mb-4">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#8B7355] font-semibold capitalize">{p.category_slug?.replace(/-/g," ")}</span>
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl leading-[0.95] text-[#1C1917]">{p.name}</h1>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex text-[#D4AF37]">{Array.from({length:5}).map((_,i)=><Star key={i} className={`w-4 h-4 ${i < Math.round(p.rating) ? "fill-current":""}`}/>)}</div>
              <span className="text-sm text-stone-600 font-outfit"><span className="font-bold">{p.rating}</span> · {p.review_count} reviews</span>
              {p.sold_count > 100 && <span className="text-xs text-[#2E4A35] font-outfit bg-[#E8EDDF] px-2 py-0.5 rounded-full">{p.sold_count}+ sold</span>}
            </div>
            <p className="mt-6 text-stone-700 text-lg leading-relaxed">{p.description}</p>

            <div className="mt-8">
              <div className="text-xs uppercase tracking-[0.25em] text-[#8B7355] mb-3 font-semibold">Choose size</div>
              <div className="grid grid-cols-3 gap-3">
                {p.variants?.map(v => (
                  <button key={v.sku} onClick={()=>setVariant(v)} data-testid={`variant-${v.sku}`}
                          className={`p-4 rounded-2xl border-2 text-left transition-all ${
                            variant?.sku===v.sku
                              ? "bg-[#3B2F2F] text-white border-[#3B2F2F] shadow-lg shadow-[#3B2F2F]/20"
                              : "bg-white border-stone-200 hover:border-[#D4AF37] hover:bg-[#FEF3E2]/50"}`}>
                    <div className={`text-xs uppercase tracking-widest ${variant?.sku===v.sku?"text-[#D4AF37]":"text-stone-400"}`}>Size</div>
                    <div className="font-serif-display text-2xl leading-tight mt-1">{v.label}</div>
                    <div className={`font-outfit font-bold text-lg mt-1 ${variant?.sku===v.sku?"text-white":"text-[#3B2F2F]"}`}>{inr(v.price)}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-stretch gap-4">
              <div className="flex items-center border-2 border-stone-200 rounded-full bg-white">
                <button onClick={()=>setQty(Math.max(1,qty-1))} data-testid="qty-dec" className="p-3 hover:bg-[#F5F0E6] rounded-l-full"><Minus className="w-4 h-4"/></button>
                <span className="px-4 font-outfit font-bold text-lg min-w-[3rem] text-center" data-testid="qty-value">{qty}</span>
                <button onClick={()=>setQty(qty+1)} data-testid="qty-inc" className="p-3 hover:bg-[#F5F0E6] rounded-r-full"><Plus className="w-4 h-4"/></button>
              </div>
              <button onClick={()=>{
                if (!variant) return;
                if (stock <= 0) { toast.error("Out of stock"); return; }
                add(p, variant, qty);
                toast.success(`Added ${qty} × ${p.name}`);
              }} disabled={stock<=0} data-testid="add-to-cart"
                      className="flex-1 bg-[#3B2F2F] text-white rounded-full py-3 px-6 font-medium hover:bg-black disabled:bg-stone-400 shadow-lg shadow-[#3B2F2F]/20 hover:shadow-xl transition-shadow">
                {stock<=0 ? "Out of stock" : `Add to bag · ${inr((variant?.price||0) * qty)}`}
              </button>
              <WishlistHeart productId={p.product_id}/>
            </div>
            {stock > 0 && stock < 10 && (
              <div className="mt-3 flex items-center gap-2 text-sm text-[#993322] font-outfit font-semibold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#993322] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#993322]"></span>
                </span>
                Only {stock} left in stock — order soon
              </div>
            )}

            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                {icon: Truck, title: "Free ship", sub: "Over ₹500", color:"#D4AF37", bg:"#FEF3E2"},
                {icon: Leaf, title: "Fresh weekly", sub: "Small batch", color:"#2E4A35", bg:"#E8EDDF"},
                {icon: ShieldCheck, title: "Assured", sub: "Quality checked", color:"#993322", bg:"#F5DEC7"},
              ].map((f,i)=>(
                <div key={i} className="rounded-2xl p-3 border border-stone-100 bg-white flex flex-col items-center text-center">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2" style={{background: f.bg}}>
                    <f.icon className="w-5 h-5" style={{color: f.color}}/>
                  </div>
                  <div className="text-xs font-outfit font-semibold text-stone-800">{f.title}</div>
                  <div className="text-[10px] text-stone-500">{f.sub}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 bg-white rounded-2xl border border-stone-100 divide-y divide-stone-100">
              <DetailRow icon={Package} label="Ingredients" value={p.ingredients || "—"}/>
              <DetailRow icon={Award} label="Storage" value={p.storage || "Store in a cool, dry place. Reseal after use."}/>
              <DetailRow icon={Clock} label="Shelf life" value={p.shelf_life || "12 months from packaging."}/>
            </div>
          </div>
        </div>
      </div>

      <Reviews product={p} onRatingUpdated={() => api.get(`/products/${slug}`).then(r => setP(r.data)).catch(()=>{})} />

      {related.length > 0 && (
        <section className="section-cream mt-20 py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="mb-10">
              <div className="uppercase text-xs tracking-[0.3em] text-[#8B7355] mb-3">Pairs beautifully with</div>
              <h2 className="font-serif-display text-4xl md:text-5xl">You may also love</h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
              {related.map(r => <ProductCard key={r.product_id} p={r} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="p-4 flex items-start gap-3">
      <div className="w-8 h-8 rounded-lg bg-[#F5F0E6] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[#8B7355]"/>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold">{label}</div>
        <div className="text-sm text-stone-700 mt-0.5">{value}</div>
      </div>
    </div>
  );
}
